import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { TipoMargem } from "@atlas/types";
import { ApiHttpError } from "@atlas/sdk";

// Limites regulatórios da margem consignável — replicados aqui para não puxar
// @atlas/domain (que não é dep do @atlas/web). Fonte da verdade continua no
// domain package; se mudar lá, atualizar aqui.
const LIMITES_MARGEM: Record<TipoMargem, number> = {
  EMPRESTIMO: 0.35,
  CARTAO_CONSIGNADO: 0.05,
  CARTAO_BENEFICIOS: 0.05,
};
const margemTotal = (salario: number, tipo: TipoMargem) => salario * LIMITES_MARGEM[tipo];
const margemDisponivel = (salario: number, comprometido: number, tipo: TipoMargem) =>
  Math.max(0, margemTotal(salario, tipo) - comprometido);
import {
  Button,
  ContratosTable,
  FormActions,
  FormGrid,
  MargemCalculadorBox,
  MargemProjecaoLinha,
  NumberField,
  SelectField,
  TextareaField,
  fmtBRL as fmtBRLUi,
} from "@atlas/ui/web";
import { atlas } from "../../../lib/sdk";
import { fmtBRL } from "../../../lib/banco-propostas";
import { formatBackendErr } from "../../../lib/format-backend-err";

/** Mapeia tipoContrato do backend para o TipoMargem do domain. */
function tipoContratoParaMargem(tipoContrato: string): TipoMargem {
  const t = tipoContrato.toUpperCase();
  if (t === "ECONSIGNADO" || t === "CARTAO_CONSIGNADO") return "CARTAO_CONSIGNADO";
  if (t === "CARTAO_BENEFICIOS") return "CARTAO_BENEFICIOS";
  return "EMPRESTIMO"; // EMPRESTIMO, REFIN → margem de empréstimo
}

/** Contratos "vivos" que ainda consomem margem. Espelha EXATAMENTE o
 *  comprometeMargem de @atlas/domain (contrato.ts) — nao dá pra importar o domain
 *  no @atlas/web, entao replicamos; se mudar la, atualizar aqui. Regra: consome
 *  por padrao, exceto os estados terminais/recusados/falha/cobranca. Bug 30/07/2026:
 *  a versao antiga NAO reconhecia "Aprovado", entao um contrato Aprovado consumia
 *  margem no card (comprometeMargem no backend) mas SUMIA da tabela — a ficha
 *  mostrava "sem contratos ativos" com margem utilizada. Agora batem. */
function consumeMargem(situacao: string): boolean {
  const s = situacao.toLowerCase();
  if (s === "expirado" || s === "cancelado" || s === "quitado") return false;
  if (s.includes("recus") || s.includes("reprov") || s.includes("rejeit") || s.includes("negad") || s.includes("estorn")) return false;
  if (s.includes("falha em folha")) return false;
  if (s.includes("cobran")) return false;
  return true;
}

/** Parseia lancamento em DD/MM/YYYY (formato do backend) ou ISO. 0 se falhar. */
function parseDataContrato(raw?: string | null): number {
  if (!raw) return 0;
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(raw);
  if (m) return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1])).getTime();
  const iso = new Date(raw).getTime();
  return Number.isNaN(iso) ? 0 : iso;
}

/** Vencimento final do contrato = data da ULTIMA parcela. Parcela 1 cai no mes
 *  do lancamento (mesma convencao do painel do servidor e da carteira), entao a
 *  ultima cai em lancamento + (totalParcelas - 1) meses, com o dia do lancamento
 *  clampado ao ultimo dia do mes. O campo `expiracao` do backend so existe pra
 *  PRE-RESERVA (validade +48h); pra contrato averbado ele e null — por isso a
 *  coluna aparecia "—/—/—". */
function vencimentoFinal(lancamento?: string | null, totalParcelas?: number): string {
  const t = parseDataContrato(lancamento);
  if (!t || !totalParcelas || totalParcelas <= 0) return "—/—/—";
  const base = new Date(t);
  const alvo = new Date(base.getFullYear(), base.getMonth() + (totalParcelas - 1), 1);
  const ultimoDia = new Date(alvo.getFullYear(), alvo.getMonth() + 1, 0).getDate();
  const dia = Math.min(base.getDate(), ultimoDia);
  return `${String(dia).padStart(2, "0")}/${String(alvo.getMonth() + 1).padStart(2, "0")}/${alvo.getFullYear()}`;
}

export function BancoMargemContratacaoFicha() {
  const { idMatricula = "" } = useParams<{ idMatricula: string }>();
  const [sp] = useSearchParams();
  // Convenio vem do seletor (index.tsx) quando o servidor tem a mesma matricula
  // em >1 prefeitura. Sem isso, o backend nao consegue distinguir qual carregar.
  const idConvenioParam = sp.get("convenio") ?? undefined;
  const nav = useNavigate();
  const qc = useQueryClient();
  const [modalAverbar, setModalAverbar] = useState<null | "aberto" | "sucesso">(null);
  const [sucessoAverbar, setSucessoAverbar] = useState<{ adf: string; exigeSenhaServidor: boolean } | null>(null);
  const [tipoInicial, setTipoInicial] = useState<TipoMargem>("EMPRESTIMO");

  const ficha = useQuery({
    queryKey: ["banco", "margem", idMatricula, idConvenioParam ?? null],
    queryFn: () => atlas.banco.margemBuscar({
      matricula: idMatricula.replace(/^MAT-/, ""),
      idConvenio: idConvenioParam,
    }),
    enabled: !!idMatricula,
  });

  const contratos = useQuery({
    queryKey: ["banco", "contratos", idMatricula],
    queryFn: () =>
      // SO os contratos gerados por ESTA matricula (idMatricula) no convenio
      // ativo. Nunca por CPF, nem de outra matricula do mesmo CPF (mesmo na
      // mesma prefeitura), nem de outra prefeitura. Cliente reportou 30/07/2026:
      // o banco logado em Guarulhos via os contratos do mesmo CPF em Capistrano.
      // O backend ja escopa com listContratos({ convenioId: ativo, matricula })
      // (matricula EXATA, store.ts:297) — basta NAO pedir incluir_todos_convenios,
      // que era justamente o que anexava os contratos das outras matriculas/pref.
      atlas.banco.contratos({ colaborador: idMatricula.replace(/^MAT-/, "") }),
    enabled: !!idMatricula,
  });

  const hoje = new Date();
  const [mes, setMes] = useState<string>(monthLabel(hoje.getMonth() + 1));
  const [ano, setAno] = useState<number>(hoje.getFullYear());
  const calcular = useMutation({
    mutationFn: () => atlas.banco.margemCalcular(idMatricula, { mes: monthIndex(mes), ano }),
  });

  // Comprometido por tipo de margem, com base nos contratos vivos do servidor.
  const comprometidoPorTipo = useMemo(() => {
    const acc: Record<TipoMargem, number> = { EMPRESTIMO: 0, CARTAO_CONSIGNADO: 0, CARTAO_BENEFICIOS: 0 };
    for (const c of contratos.data?.contratos ?? []) {
      if (!consumeMargem(c.situacao)) continue;
      acc[tipoContratoParaMargem(c.tipoContrato)] += c.valorParcela;
    }
    return acc;
  }, [contratos.data]);

  if (ficha.isLoading) return <div style={{ color: "var(--text-muted)" }}>Carregando ficha...</div>;
  if (ficha.error || !ficha.data) return <div style={{ color: "var(--danger-500)" }}>Erro ao carregar servidor.</div>;
  // ficha vira opcional pos-29/07/2026 (backend retorna `matriculas` quando >1),
  // mas nesta rota so cai aqui apos idMatricula ser fixado — sempre retorna ficha.
  if (!ficha.data.ficha) return <div style={{ color: "var(--danger-500)" }}>Ficha nao carregada.</div>;

  const f = ficha.data.ficha;
  // LGPD: banco NAO recebe salarioLiquido — usa `margens` pré-calculadas
  // pelo backend (contem total/utilizado/disponivel por bucket).
  // Fallback pro calculo local so quando o backend nao trouxer (SDK antigo).
  const labelOf = (t: TipoMargem): string =>
    t === "EMPRESTIMO"
      ? "Empréstimo Consignado"
      : t === "CARTAO_CONSIGNADO"
        ? "Cartão de Crédito Consignado"
        : "Cartão Benefício Consignado";
  const margens: { tipo: TipoMargem; label: string; total: number; utilizado: number; disponivel: number }[] =
    f.margens && f.margens.length > 0
      ? f.margens.map((m) => ({ ...m, label: labelOf(m.tipo) }))
      : (["EMPRESTIMO", "CARTAO_CONSIGNADO", "CARTAO_BENEFICIOS"] as const).map((t) => {
          const salario = 0; // sem backend, nao ha como calcular — retorna zero
          const utilizado = comprometidoPorTipo[t];
          return {
            tipo: t,
            label: labelOf(t),
            total: margemTotal(salario, t),
            utilizado,
            disponivel: margemDisponivel(salario, utilizado, t),
          };
        });

  // Contratos "ativos do servidor" para a tabela do modelo (apenas os que ainda vivem).
  const contratosAtivos = (contratos.data?.contratos ?? []).filter((c) => consumeMargem(c.situacao));
  const situacaoFuncional = (f.situacaoFuncional || "").toLowerCase();
  const isAtivo = situacaoFuncional === "ativo" || situacaoFuncional === "trabalhando";

  const calc = calcular.data ?? null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 1200 }}>
      {/* Header no modelo: nome grande + subtítulo (mat, cpf mascarado, situação/origem) + pills à direita */}
      <header style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
            Margem / Contratação
          </span>
          <h1 style={{ margin: "4px 0 0", fontSize: "1.8rem", letterSpacing: "-0.02em" }}>{f.nome}</h1>
          <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 4 }}>
            Mat: <span style={{ fontFamily: "var(--font-mono)" }}>{f.matricula}</span>
            {" · "}CPF: <span style={{ fontFamily: "var(--font-mono)" }}>{f.cpfMasked}</span>
            {f.origem ? <> · {f.origem}</> : null}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <StatusPill on={isAtivo}>{isAtivo ? "● Ativo" : f.situacaoFuncional || "Situação"}</StatusPill>
          <StatusPill neutro>{f.vinculo || "Efetivo"}</StatusPill>
          <button
            type="button"
            onClick={() => nav("/banco/margem-contratacao")}
            style={{
              background: "transparent",
              border: 0,
              color: "var(--accent)",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            ← Nova busca
          </button>
        </div>
      </header>

      {/* 3 cards de margem lado a lado — modelo da screenshot */}
      <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16 }}>
        {margens.map((m) => (
          <MargemTipoCard key={m.tipo} label={m.label} total={m.total} utilizado={m.utilizado} disponivel={m.disponivel} />
        ))}
      </section>

      {modalAverbar === "aberto" ? (
        <AverbarMargemModal
          idMatricula={idMatricula}
          idConvenio={f.idConvenio}
          nomeServidor={f.nome}
          margens={margens}
          tipoInicial={tipoInicial}
          exigeSenhaServidor={f.exigeSenhaAverbacao}
          tabelaVigente={f.tabelaVigente}
          onClose={() => setModalAverbar(null)}
          onSuccess={(r) => {
            setSucessoAverbar(r);
            setModalAverbar("sucesso");
            qc.invalidateQueries({ queryKey: ["banco", "contratos", idMatricula] });
            qc.invalidateQueries({ queryKey: ["banco", "margem", idMatricula] });
          }}
        />
      ) : null}
      {modalAverbar === "sucesso" && sucessoAverbar ? (
        <AverbarSucessoModal
          adf={sucessoAverbar.adf}
          exigeSenhaServidor={sucessoAverbar.exigeSenhaServidor}
          onClose={() => { setModalAverbar(null); setSucessoAverbar(null); }}
        />
      ) : null}

      {/* Contratos ativos do servidor — tabela do modelo */}
      <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ fontSize: 11, letterSpacing: "0.08em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
          Contratos ativos do servidor
        </div>
        <ContratosTable
          loading={contratos.isLoading}
          rows={contratosAtivos.map((c) => ({
            adf: c.adf,
            situacao: c.situacao,
            lancamento: c.lancamento,
            // Reserva mostra a validade (+48h); contrato averbado mostra o
            // vencimento final (ultima parcela), que o backend nao envia.
            expiracao: c.expiracao ?? vencimentoFinal(c.lancamento, c.totalParcelas),
            tipoContrato: c.tipoContrato,
            totalParcelas: c.totalParcelas,
            valorParcela: c.valorParcela,
            convenio: c.convenio,
          }))}
          emptyState="Este servidor não possui contratos ativos."
        />
      </section>

      {/* Averbacao INICIADA PELO BANCO — bloco grande, entre contratos ativos
          e a simulacao. Cada bucket abre o modal com o tipo pre-selecionado.
          Cliente pediu 30/07/2026 o retorno do layout maior. */}
      <section
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 16,
          padding: 20,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.08em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
              Averbação iniciada pelo banco
            </div>
            <h2 style={{ margin: "4px 0 0", fontSize: 18 }}>Averbar margem do servidor</h2>
            <p style={{ margin: "6px 0 0", color: "var(--text-muted)", fontSize: 13, lineHeight: 1.5, maxWidth: 640 }}>
              Escolha o bucket de margem que vai ser consumido e informe valor e parcelas.
              {f.tabelaVigente
                ? <> Taxa e prazo máximo saem da <b>tabela vigente do banco</b> ({(f.tabelaVigente.taxaAm * 100).toFixed(2)}% a.m. · até {f.tabelaVigente.prazoMaxMeses}×).</>
                : <> <b style={{ color: "var(--danger-500)" }}>Sem tabela publicada pra este convênio</b> — cadastre em Cadastros / Tabelas antes de averbar.</>}
              {f.exigeSenhaAverbacao ? " A prefeitura exige a senha do servidor pra concluir." : ""}
            </p>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
          {margens.map((m) => (
            <AverbarBucketCard
              key={m.tipo}
              label={m.label}
              disponivel={m.disponivel}
              disabled={m.disponivel <= 0 || !f.tabelaVigente}
              onClick={() => {
                setTipoInicial(m.tipo);
                setSucessoAverbar(null);
                setModalAverbar("aberto");
              }}
            />
          ))}
        </div>
      </section>

      {/* Simulação avançada — mantida como opcional pra recalcular por competência */}
      <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 16, color: "var(--text-muted)" }}>Simulação por competência</h2>
        <MargemCalculadorBox
          mes={mes}
          ano={ano}
          onMesChange={setMes}
          onAnoChange={setAno}
          total={calc?.total ?? null}
          disponivel={calc?.disponivel ?? null}
          tipo="EMPRESTIMO"
          calculando={calcular.isPending}
          onCalcular={() => calcular.mutate()}
        />
        {calc ? <MargemProjecaoLinha meses={calc.projecao} /> : null}
      </section>
    </div>
  );
}

/** Card do modelo: título + pill "● Disponível" + progress bar + TOTAL/UTILIZADO/DISPONÍVEL. */
function MargemTipoCard({
  label,
  total,
  utilizado,
  disponivel,
}: {
  label: string;
  total: number;
  utilizado: number;
  disponivel: number;
}) {
  const pct = total > 0 ? Math.min(1, utilizado / total) : 0;
  const semUso = utilizado === 0 && total > 0;
  return (
    <article
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 14,
        padding: 18,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)" }}>{label}</div>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "var(--emerald-500)",
            border: "1px solid var(--emerald-500)",
            borderRadius: 999,
            padding: "2px 10px",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          ● Disponível
        </span>
      </div>
      {/* Progress bar mostrando utilizado / total */}
      <div style={{ height: 6, borderRadius: 6, background: "var(--bg-elev-2)", overflow: "hidden", marginBottom: 14 }}>
        <div
          style={{
            width: `${Math.round(pct * 100)}%`,
            height: "100%",
            background: semUso ? "var(--emerald-500)" : "var(--gold-500)",
          }}
        />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
        <ValorLinha label="TOTAL" value={fmtBRL(total)} color="var(--text)" />
        <ValorLinha label="UTILIZADO" value={fmtBRL(utilizado)} color={utilizado > 0 ? "var(--gold-500)" : "var(--text-muted)"} />
        <ValorLinha label="DISPONÍVEL" value={fmtBRL(disponivel)} color="var(--emerald-500)" bold />
      </div>
    </article>
  );
}

function ValorLinha({ label, value, color, bold }: { label: string; value: string; color: string; bold?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 10, letterSpacing: "0.06em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{ fontSize: 15, fontWeight: bold ? 800 : 700, color, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function StatusPill({ on, neutro, children }: { on?: boolean; neutro?: boolean; children: React.ReactNode }) {
  const cor = neutro ? "var(--text-muted)" : on ? "var(--emerald-500)" : "var(--gold-500)";
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: cor,
        border: `1px solid ${cor}`,
        borderRadius: 999,
        padding: "3px 10px",
      }}
    >
      {children}
    </span>
  );
}

/** Card grande de um bucket de margem no bloco "Averbar operação". Clique
 *  abre o modal de averbação com o tipo desse bucket pre-selecionado. */
function AverbarBucketCard({
  label,
  disponivel,
  disabled,
  onClick,
}: {
  label: string;
  disponivel: number;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        textAlign: "left",
        background: "var(--bg-elev)",
        border: "1px solid var(--border)",
        borderRadius: 12,
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        color: "var(--text)",
        font: "inherit",
        transition: "border-color .15s, transform .15s",
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.borderColor = "var(--gold-500)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--border)"; }}
    >
      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{label}</div>
      <div>
        <div style={{ fontSize: 10, letterSpacing: "0.06em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
          Disponível
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color: disponivel > 0 ? "var(--emerald-500)" : "var(--text-muted)", marginTop: 2 }}>
          {fmtBRL(disponivel)}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
          {disabled ? "Sem margem" : "Iniciar averbação"}
        </span>
        <span style={{ fontSize: 13, fontWeight: 700, color: disabled ? "var(--text-muted)" : "var(--gold-500)" }}>
          {disabled ? "—" : "Averbar →"}
        </span>
      </div>
    </button>
  );
}

const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
function monthLabel(m: number): string { return MESES[m - 1] ?? "Janeiro"; }
function monthIndex(label: string): number { return MESES.indexOf(label) + 1 || 1; }

/** Modal "Averbar margem" — feature 4+6 (cliente 29/07/2026). Escolhe bucket,
 *  valor, parcelas, taxa. Se a prefeitura exigir senha do servidor, contrato
 *  vira "Aguardando Confirmacao do Servidor"; senao, entra Ativo direto. */
function AverbarMargemModal({
  idMatricula,
  idConvenio,
  nomeServidor,
  margens,
  tipoInicial,
  exigeSenhaServidor,
  tabelaVigente,
  onClose,
  onSuccess,
}: {
  idMatricula: string;
  idConvenio: string;
  nomeServidor: string;
  margens: { tipo: TipoMargem; label: string; total: number; utilizado: number; disponivel: number }[];
  tipoInicial: TipoMargem;
  /** Vem da prefeitura do servidor via margemBuscar. Quando true, o modal
   *  mostra o campo de senha e o backend valida antes de averbar. */
  exigeSenhaServidor: boolean;
  /** Tabela ATIVA do banco pro convenio do servidor. Dita o teto de parcelas
   *  e a taxa aplicada (banco nao escolhe). null = sem tabela publicada. */
  tabelaVigente: { prazoMaxMeses: number; taxaAm: number } | null;
  onClose: () => void;
  onSuccess: (r: { adf: string; exigeSenhaServidor: boolean }) => void;
}) {
  const parcelasMax = tabelaVigente?.prazoMaxMeses ?? 0;
  const taxaAmVigente = tabelaVigente?.taxaAm ?? 0;
  const parcelasIniciais = Math.min(24, parcelasMax || 24);
  const [tipo, setTipo] = useState<TipoMargem>(tipoInicial);
  const [valor, setValor] = useState<number>(1000);
  const [parcelas, setParcelas] = useState<number>(parcelasIniciais);
  const [observacoes, setObservacoes] = useState<string>("");
  const [senhaServidor, setSenhaServidor] = useState<string>("");
  const [erro, setErro] = useState<string | null>(null);
  const margemAtual = margens.find((m) => m.tipo === tipo);
  const semTabela = tabelaVigente == null;
  const excedeParcelas = parcelasMax > 0 && parcelas > parcelasMax;
  const mut = useMutation({
    mutationFn: () =>
      atlas.banco.averbarDireto(idMatricula, {
        tipo,
        valor,
        parcelas,
        observacoes: observacoes.trim() || undefined,
        // Passa idConvenio pra desambiguar quando a matricula existe em >1
        // convenio (mesmo CPF em 2 prefeituras com mesma numeracao de matricula).
        idConvenio,
        // Senha do servidor — obrigatoria quando a prefeitura exige.
        senhaServidor: exigeSenhaServidor ? senhaServidor : undefined,
      }),
    onSuccess: (r) => onSuccess({ adf: r.contrato.adf, exigeSenhaServidor: r.contrato.exigeSenhaServidor }),
    onError: (err) => setErro(formatBackendErr(err)),
  });
  return (
    <div style={modalBackdrop} onMouseDown={(e) => { if (e.target === e.currentTarget && !mut.isPending) onClose(); }}>
      <div style={modalCard} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0 }}>Averbar margem</h3>
        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
          Servidor: <b>{nomeServidor}</b>
        </div>
        <FormGrid cols={2}>
          <SelectField
            label="Tipo de margem"
            value={tipo}
            onChange={(e) => setTipo(e.target.value as TipoMargem)}
            options={margens.map((m) => ({ value: m.tipo, label: `${m.label} — disp. ${fmtBRLUi(m.disponivel)}` }))}
          />
          <NumberField
            label="Valor financiado (R$)"
            step={100}
            value={valor}
            onChange={(e) => setValor(Number(e.target.value))}
          />
          <NumberField
            label={`Parcelas${parcelasMax > 0 ? ` (máx. ${parcelasMax}×)` : ""}`}
            step={1}
            min={1}
            max={parcelasMax > 0 ? parcelasMax : undefined}
            value={parcelas}
            onChange={(e) => setParcelas(Number(e.target.value))}
          />
          {/* Taxa e' READ-ONLY: vem da tabela publicada pelo banco pra este
              convenio. Cliente pediu 30/07/2026 que o banco nao possa mais
              digitar taxa livre no modal — mesma logica do simulador do
              servidor. Backend tambem ignora body.taxaAm. */}
          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.06em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", marginBottom: 6 }}>
              Taxa (% a.m.) — tabela vigente
            </div>
            <div
              style={{
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid var(--border)",
                background: "var(--bg-elev-2)",
                color: tabelaVigente ? "var(--text)" : "var(--text-muted)",
                fontSize: 15,
                fontWeight: 700,
                fontFamily: "var(--font-mono)",
              }}
            >
              {tabelaVigente ? `${(taxaAmVigente * 100).toFixed(2)}%` : "—"}
            </div>
          </div>
        </FormGrid>
        <TextareaField
          label="Observações (opcional)"
          value={observacoes}
          onChange={(e) => setObservacoes(e.target.value)}
          rows={2}
          maxLength={300}
        />
        {margemAtual ? (
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Margem disponível deste bucket: <b>{fmtBRLUi(margemAtual.disponivel)}</b>.
          </div>
        ) : null}
        {semTabela ? (
          <div style={{ padding: 12, borderRadius: 8, background: "color-mix(in srgb, var(--danger-500) 10%, transparent)", border: "1px solid var(--danger-500)", fontSize: 13, color: "var(--text)" }}>
            Este banco ainda não publicou tabela ativa para o convênio do servidor.
            Cadastre uma tabela em <b>Cadastros / Tabelas de empréstimo</b> antes de averbar.
          </div>
        ) : excedeParcelas ? (
          <div style={{ padding: 10, borderRadius: 8, background: "color-mix(in srgb, var(--gold-500) 10%, transparent)", border: "1px solid color-mix(in srgb, var(--gold-500) 40%, transparent)", fontSize: 12, color: "var(--text)" }}>
            Prazo máximo pra este convênio é <b>{parcelasMax}×</b> — reduza as parcelas pra prosseguir.
          </div>
        ) : null}
        {exigeSenhaServidor ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: 12, borderRadius: 8, background: "color-mix(in srgb, var(--gold-500) 8%, transparent)", border: "1px solid color-mix(in srgb, var(--gold-500) 40%, transparent)" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--gold-500)", letterSpacing: "0.05em", textTransform: "uppercase" }}>
              Prefeitura exige senha do servidor
            </div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.4 }}>
              Peça ao servidor pra digitar a senha dele aqui. Sem ela, a averbação é rejeitada.
            </div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 4 }}>
              Senha do servidor
            </label>
            <input
              type="password"
              value={senhaServidor}
              onChange={(e) => setSenhaServidor(e.target.value)}
              autoComplete="off"
              style={{ padding: "8px 10px", borderRadius: 6, border: "1px solid var(--border-strong)", background: "var(--bg-elev)", color: "var(--text)", fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" }}
            />
          </div>
        ) : null}
        {erro ? (
          <div style={{ padding: "8px 12px", borderRadius: 8, background: "color-mix(in srgb, var(--danger-500) 10%, transparent)", border: "1px solid var(--danger-500)", fontSize: 13, color: "var(--danger-500)" }}>
            {erro}
          </div>
        ) : null}
        <FormActions>
          <Button variant="ghost" type="button" onClick={onClose} disabled={mut.isPending}>Cancelar</Button>
          <Button
            type="button"
            onClick={() => { setErro(null); mut.mutate(); }}
            disabled={mut.isPending || valor <= 0 || parcelas <= 0 || semTabela || excedeParcelas || (exigeSenhaServidor && !senhaServidor)}
          >
            {mut.isPending ? "Averbando..." : "Averbar →"}
          </Button>
        </FormActions>
      </div>
    </div>
  );
}

function AverbarSucessoModal({
  adf,
  exigeSenhaServidor,
  onClose,
}: {
  adf: string;
  exigeSenhaServidor: boolean;
  onClose: () => void;
}) {
  return (
    <div style={modalBackdrop} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalCard} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0, color: "var(--emerald-500)" }}>Averbação ativa</h3>
        <div style={{ fontSize: 14, color: "var(--text-muted)" }}>
          Contrato <code style={{ fontFamily: "var(--font-mono)" }}>{adf}</code> criado.
        </div>
        <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.5 }}>
          {exigeSenhaServidor ? (
            <>O servidor autorizou a operação com a senha dele. Contrato entrou <b>Ativo</b> — a averbação segue o fluxo normal de folha.</>
          ) : (
            <>A prefeitura não exige senha do servidor pra esta operação — o contrato entrou direto como <b>Ativo</b> e a averbação segue o fluxo normal de folha.</>
          )}
        </div>
        <FormActions>
          <Button type="button" onClick={onClose}>OK</Button>
        </FormActions>
      </div>
    </div>
  );
}

const modalBackdrop: React.CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(10,22,40,.6)",
  display: "grid", placeItems: "center", zIndex: 100, backdropFilter: "blur(6px)",
};
const modalCard: React.CSSProperties = {
  background: "var(--bg-elev)", border: "1px solid var(--border-strong)",
  borderRadius: 14, padding: 24, maxWidth: 560, width: "calc(100% - 48px)",
  display: "flex", flexDirection: "column", gap: 16, boxShadow: "var(--shadow-lg)",
};
